import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { uuidv7, type Channel, type ChannelMessage, type CommunityStructure, type UserSummary } from "@atrium/shared";
import { ApiError, api, errorMessage, qs } from "../../lib/api";
import { useMe } from "../auth/AuthProvider";
import { Button, IconButton } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/Controls";
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from "../../components/ui/Menu";
import { toast } from "../../components/ui/Toast";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconBack, IconEdit, IconLock, IconMore, IconPin, IconPinFilled, IconTrash, IconUnlock, IconX } from "../../components/icons";
import { MessageList } from "../chat/MessageList";
import { ChatComposer, type ComposerSend } from "../chat/ChatComposer";
import { gifEmbed } from "../chat/ChatGif";
import { chatQueryOptions, patchMessage, removeMessage, upsertMessage, type MessagePage } from "../chat/cache";
import { deleteMessageWithUndo } from "../chat/deleteWithUndo";
import type { ChatMessage, MessageActions } from "../chat/types";
import { permsOf, useMembers } from "../communities/hooks";
import { roleColour } from "../communities/MemberList";
import { PostDialog, TagChip, forumPerms } from "./ForumView";
import { threadMessagesKey, useThread } from "./hooks";
import { ThreadContextMenu, useThreadActions } from "./ThreadMenu";

type Page = MessagePage<ChannelMessage & { pending?: boolean; failed?: boolean }>;

/** One forum post: its messages, scoped by threadId, with the usual list and composer. */
export function ThreadView({
  structure,
  channel,
  threadId,
  onClose,
  mobile,
}: {
  structure: CommunityStructure;
  channel: Channel;
  threadId: string;
  onClose: () => void;
  mobile?: boolean;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const { community } = structure;
  const perms = permsOf(channel.permissions, community.isOwner);
  const fp = forumPerms(channel, community.isOwner);
  const timedOut = !!community.timeoutUntil && new Date(community.timeoutUntil) > new Date();
  const { data: thread, error } = useThread(threadId);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState(false);
  const key = useMemo(() => threadMessagesKey(channel.id, threadId), [channel.id, threadId]);
  const tags = new Map(channel.forumTags.map((t) => [t.id, t]));

  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => api.get<Page>(`/api/channels/${channel.id}/messages${qs({ threadId, before: pageParam as string | undefined, limit: 50 })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMoreBefore ? last.items.find((m) => !m.id.startsWith("pending-"))?.id : undefined),
    ...chatQueryOptions,
  });
  const messages = useMemo(() => (query.data ? [...query.data.pages].reverse().flatMap((p) => p.items) : []), [query.data]);

  const { data: memberPages } = useMembers(community.id);
  const colourMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const m of memberPages?.pages.flatMap((p) => p.items) ?? []) map.set(m.user.id, roleColour(m.roleIds, structure.roles));
    return map;
  }, [memberPages, structure.roles]);

  const send = useCallback(
    async ({ content, mediaIds, gif }: ComposerSend, retryNonce?: string) => {
      const nonce = retryNonce ?? uuidv7();
      const reply = replyTo;
      setReplyTo(null);
      const optimistic: ChannelMessage & { pending: boolean; failed: boolean } = {
        id: `pending-${nonce}`,
        channelId: channel.id,
        communityId: community.id,
        author: { id: me.id, username: me.username, displayName: me.displayName, avatarUrl: me.avatarUrl },
        content,
        createdAt: new Date().toISOString(),
        editedAt: null,
        pinnedAt: null,
        replyTo: reply ? { id: reply.id, author: reply.author, content: reply.content, hasMedia: reply.media.length > 0 } : null,
        media: [],
        kind: "default",
        threadId,
        embeds: gif ? [gifEmbed(gif)] : [],
        components: [],
        forwardedFrom: null,
        stickers: [],
        webhook: null,
        interaction: null,
        suppressEmbeds: false,
        reactions: [],
        mentionIds: [],
        mentionsEveryone: false,
        publishedPostId: null,
        nonce,
        pending: true,
        failed: false,
      };
      if (!retryNonce) upsertMessage(key, optimistic);
      else patchMessage<typeof optimistic>(key, `pending-${nonce}`, (m) => ({ ...m, failed: false, pending: true }));
      try {
        const msg = await api.post<ChannelMessage>(`/api/channels/${channel.id}/messages`, { content, mediaIds, replyToId: reply?.id, nonce, threadId, gif });
        upsertMessage(key, msg);
      } catch (e) {
        patchMessage<typeof optimistic>(key, `pending-${nonce}`, (m) => ({ ...m, failed: true, pending: false }));
        toast.error(errorMessage(e, "Message not sent"));
      }
    },
    [channel.id, community.id, key, me, replyTo, threadId],
  );

  const actions: MessageActions = {
    authorColour: (id) => colourMap.get(id) ?? null,
    canReply: perms.send && !timedOut && (!thread?.locked || fp.manage),
    canReact: perms.react && !timedOut,
    canPin: false,
    canModerate: perms.manageMessages,
    onReply: setReplyTo,
    onReact: (m, emoji, add) => {
      patchMessage<ChannelMessage>(key, m.id, (x) => {
        const existing = x.reactions.find((r) => r.emoji === emoji);
        const reactions = existing
          ? x.reactions.map((r) => (r.emoji === emoji ? { ...r, me: add, count: r.count + (add ? 1 : -1) } : r)).filter((r) => r.count > 0)
          : add
            ? [...x.reactions, { emoji, count: 1, me: true }]
            : x.reactions;
        return { ...x, reactions };
      });
      api.put(`/api/messages/${m.id}/reactions`, { emoji, value: add }).catch((e) => toast.error(errorMessage(e)));
    },
    canAttach: perms.attach,
    onEdit: async (m, content, mediaIds) => {
      try {
        const updated = await api.patch<ChannelMessage>(`/api/messages/${m.id}`, { content, ...(mediaIds?.length ? { mediaIds } : {}) });
        patchMessage<ChannelMessage>(key, m.id, () => updated);
      } catch (e) {
        toast.error(errorMessage(e, "Couldn't edit"));
        throw e;
      }
    },
    onDelete: async (m) => {
      if (m.failed) return removeMessage(key, m.id);
      deleteMessageWithUndo(key, m.id, () => api.del(`/api/messages/${m.id}`));
    },
    onRetry: (m) => void send({ content: m.content, mediaIds: [] }, m.nonce),
  };

  const rawRefs = useMemo(() => ({ roles: structure.roles.filter((r) => !r.isDefault), channels: structure.channels }), [structure.roles, structure.channels]);

  const mentionSource = useCallback(
    async (q: string) => {
      try {
        return (await api.get<{ items: UserSummary[] }>(`/api/channels/${channel.id}/mention-candidates${qs({ q })}`)).items;
      } catch {
        return [];
      }
    },
    [channel.id],
  );

  const threadActions = useThreadActions(channel, community.isOwner, thread);
  const { update } = threadActions;
  const remove = () => void threadActions.remove().then((ok) => ok && onClose());

  if (error instanceof ApiError && error.status === 404)
    return (
      <div className="flex min-w-0 flex-1 flex-col bg-bg">
        <EmptyState title="Post not found" description="It may have been deleted." action={<Button onClick={onClose}>Back to posts</Button>} className="flex-1" />
      </div>
    );

  const { canEdit, canDelete } = threadActions;
  const disabledReason = timedOut
    ? `You're timed out until ${new Date(community.timeoutUntil!).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}.`
    : !perms.send
      ? "You don't have permission to send messages here."
      : thread?.locked && !fp.manage
        ? "This post is locked."
        : null;

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col bg-bg">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        <IconButton label={mobile ? "Back" : "Close post"} icon={mobile ? IconBack : IconX} tooltip={!mobile} onClick={onClose} />
        {thread?.pinned && (
          <Tooltip label="Pinned">
            <IconPinFilled className="icon size-4 shrink-0 text-fg-3" aria-label="Pinned" />
          </Tooltip>
        )}
        {thread?.locked && (
          <Tooltip label="Locked">
            <IconLock className="icon size-4 shrink-0 text-fg-3" aria-label="Locked" />
          </Tooltip>
        )}
        {thread ? (
          <ThreadContextMenu thread={thread} channel={channel} communityId={community.id} isOwner={community.isOwner} onEdit={() => setEditing(true)} onDeleted={onClose}>
            <h1 className="min-w-0 truncate text-base font-semibold text-fg">{thread.title}</h1>
          </ThreadContextMenu>
        ) : (
          <h1 className="min-w-0 truncate text-base font-semibold text-fg" />
        )}
        {(canEdit || canDelete) && (
          <Menu>
            <MenuTrigger asChild>
              <IconButton label="Post options" icon={IconMore} className="ml-auto" />
            </MenuTrigger>
            <MenuContent>
              {canEdit && (
                <MenuItem icon={IconEdit} onSelect={() => setEditing(true)}>
                  Edit title and tags
                </MenuItem>
              )}
              {fp.manage && (
                <>
                  <MenuItem icon={thread!.pinned ? IconPinFilled : IconPin} onSelect={() => void update({ pinned: !thread!.pinned })}>
                    {thread!.pinned ? "Unpin" : "Pin"}
                  </MenuItem>
                  <MenuItem icon={thread!.locked ? IconUnlock : IconLock} onSelect={() => void update({ locked: !thread!.locked })}>
                    {thread!.locked ? "Unlock" : "Lock"}
                  </MenuItem>
                </>
              )}
              {canDelete && (
                <>
                  <MenuSeparator />
                  <MenuItem icon={IconTrash} danger onSelect={remove}>
                    Delete post
                  </MenuItem>
                </>
              )}
            </MenuContent>
          </Menu>
        )}
      </header>

      <MessageList
        messages={messages}
        loading={query.isPending}
        hasMore={!!query.hasNextPage}
        loadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        meId={me.id}
        meUsername={me.username}
        actions={actions}
        intro={
          <div className="px-4 pb-2 pt-8">
            <h2 className="text-2xl font-semibold text-fg">{thread?.title}</h2>
            {thread && thread.tagIds.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">{[...tags.values()].filter((t) => thread.tagIds.includes(t.id)).map((t) => <TagChip key={t.id} tag={t} size="sm" />)}</div>
            )}
          </div>
        }
      />
      {query.isError && (
        <div className="px-4 pb-2 text-sm text-danger">
          {errorMessage(query.error, "Couldn't load messages")}{" "}
          <button className="underline" onClick={() => void query.refetch()}>
            Retry
          </button>
        </div>
      )}
      <ChatComposer
        draftKey={`t:${threadId}`}
        placeholder="Reply to this post"
        disabledReason={disabledReason}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onSend={(i) => void send(i)}
        allowAttachments={perms.attach}
        allowGifs
        mentionSource={mentionSource}
        typingNames={[]}
        slowModeSeconds={perms.bypassSlowMode ? 0 : channel.slowModeSeconds}
        rawRefs={rawRefs}
      />
      {thread && <PostDialog open={editing} onOpenChange={setEditing} channel={channel} communityId={community.id} thread={thread} />}
    </div>
  );
}
